import React from 'react';
import { Box, Tooltip, Typography } from '@mui/material';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import UndoOutlinedIcon from '@mui/icons-material/UndoOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import HourglassEmptyOutlinedIcon from '@mui/icons-material/HourglassEmptyOutlined';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import { FONT_ENUM } from '../../../types/global/fonts.types';
import type { IBrandOpsContract } from '../../../redux/brandOps/types';
import { formatEscrowPayout } from '../../../redux/brandOps/brandOpsHelpers';
import { BRAND_LUXE_MUTED } from '../brandDashboardTokens';
import { ManageGoldButton } from './manageShared';

type Props = {
    periods: NonNullable<IBrandOpsContract['escrow_periods']>;
    busy: boolean;
    onAction: (action: 'lock' | 'payout' | 'release', periodId?: string) => void;
};

const C_TEAL = '#00fdc1';
const C_AMBER = '#ffc965';
const C_ERROR = '#ff7351';
const C_PURPLE = '#b47bff';

type StatusMeta = { color: string; bg: string; icon: React.ReactNode; label: string };

function getStatusMeta(status?: string): StatusMeta {
    switch (status) {
        case 'locked':
            return {
                color: C_AMBER,
                bg: 'rgba(255,201,101,0.1)',
                icon: <LockOutlinedIcon sx={{ fontSize: 13 }} />,
                label: 'Locked',
            };
        case 'paid':
        case 'released_to_manager':
            return {
                color: C_TEAL,
                bg: 'rgba(0,253,193,0.1)',
                icon: <CheckCircleOutlineIcon sx={{ fontSize: 13 }} />,
                label: 'Paid out',
            };
        case 'released':
        case 'released_to_brand':
            return {
                color: C_PURPLE,
                bg: 'rgba(180,123,255,0.1)',
                icon: <UndoOutlinedIcon sx={{ fontSize: 13 }} />,
                label: 'Released',
            };
        case 'failed':
            return {
                color: C_ERROR,
                bg: 'rgba(255,115,81,0.1)',
                icon: <ErrorOutlineIcon sx={{ fontSize: 13 }} />,
                label: 'Failed',
            };
        case 'pending_lock':
        default:
            return {
                color: BRAND_LUXE_MUTED,
                bg: 'rgba(255,255,255,0.06)',
                icon: <HourglassEmptyOutlinedIcon sx={{ fontSize: 13 }} />,
                label: status?.replace(/_/g, ' ') || 'Pending',
            };
    }
}

function fmtDate(value: string | Date | null | undefined): string {
    if (!value) return '—';
    const d = new Date(value);
    if (isNaN(d.getTime())) return '—';
    const day = d.getDate();
    const suffix =
        day % 10 === 1 && day !== 11
            ? 'st'
            : day % 10 === 2 && day !== 12
            ? 'nd'
            : day % 10 === 3 && day !== 13
            ? 'rd'
            : 'th';
    const month = d.toLocaleString('en-US', { month: 'short' });
    return `${month} ${day}${suffix}, ${d.getFullYear()}`;
}

const CURRENCY_SYMBOLS: Record<string, string> = { usd: '$', ngn: '₦', gbp: '£', eur: '€' };
function fmt(cents: number, currency?: string | null) {
    const key = (currency || 'usd').toLowerCase();
    const sym = CURRENCY_SYMBOLS[key] ?? key.toUpperCase() + ' ';
    return `${sym}${(cents / 100).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;
}

export default function ContractEscrowCard({ periods, busy, onAction }: Props) {
    return (
        <Box
            sx={{
                borderRadius: 2.5,
                overflow: 'hidden',
                bgcolor: '#1a1919',
                border: '1px solid rgba(255,255,255,0.07)',
            }}>
            {/* Header */}
            <Box
                sx={{
                    px: 2.5,
                    pt: 2.25,
                    pb: 2,
                    background: 'linear-gradient(135deg, rgba(255,201,101,0.09) 0%, rgba(0,253,193,0.05) 100%)',
                    borderBottom: '1px solid rgba(255,255,255,0.06)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.25,
                }}>
                <Box sx={{ p: 0.75, borderRadius: 1.5, bgcolor: 'rgba(255,201,101,0.15)', display: 'flex' }}>
                    <AccountBalanceWalletOutlinedIcon sx={{ color: C_AMBER, fontSize: 18 }} />
                </Box>
                <Box>
                    <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, color: '#fff', fontSize: 14, lineHeight: 1.2 }}>
                        Escrow periods
                    </Typography>
                    <Typography sx={{ color: BRAND_LUXE_MUTED, fontSize: 11, mt: 0.15 }}>
                        Funds lock automatically each period and pay out when the period ends
                    </Typography>
                </Box>
            </Box>

            {/* Body */}
            <Box sx={{ px: 2.5, py: 2 }}>
                {!periods.length ? (
                    <Box sx={{ py: 3, textAlign: 'center' }}>
                        <HourglassEmptyOutlinedIcon sx={{ fontSize: 32, color: 'rgba(255,255,255,0.1)', mb: 1 }} />
                        <Typography sx={{ color: BRAND_LUXE_MUTED, fontSize: 13 }}>
                            No escrow periods recorded yet.
                        </Typography>
                    </Box>
                ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                        {periods.map((p, i) => {
                            const paid = formatEscrowPayout(p);
                            const meta = getStatusMeta(p.status);
                            const isLocked = p.status === 'locked';
                            const canLock = p.status === 'pending_lock' || p.status === 'failed';

                            return (
                                <Box
                                    key={p._id || i}
                                    sx={{
                                        borderRadius: 2,
                                        border: `1px solid ${
                                            isLocked ? 'rgba(255,201,101,0.18)' : 'rgba(255,255,255,0.06)'
                                        }`,
                                        bgcolor: isLocked ? 'rgba(255,201,101,0.04)' : 'rgba(255,255,255,0.02)',
                                        overflow: 'hidden',
                                        transition: 'border-color 0.15s',
                                    }}>
                                    {/* Period row */}
                                    <Box sx={{ px: 1.75, pt: 1.5, pb: paid || p.failure_reason ? 1 : 1.5 }}>
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'flex-start',
                                                justifyContent: 'space-between',
                                                gap: 1,
                                                mb: 0.75,
                                            }}>
                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                <Typography
                                                    sx={{
                                                        color: '#fff',
                                                        fontSize: 13,
                                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                        lineHeight: 1.3,
                                                    }}>
                                                    {p.period_start && p.period_end
                                                        ? `${fmtDate(p.period_start)} – ${fmtDate(p.period_end)}`
                                                        : p.label || `Period ${i + 1}`}
                                                </Typography>
                                                {p.due_at && (
                                                    <Box
                                                        sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: 0.5,
                                                            mt: 0.4,
                                                        }}>
                                                        <CalendarTodayOutlinedIcon
                                                            sx={{ fontSize: 10, color: BRAND_LUXE_MUTED }}
                                                        />
                                                        <Typography sx={{ color: BRAND_LUXE_MUTED, fontSize: 11 }}>
                                                            Due {fmtDate(p.due_at)}
                                                        </Typography>
                                                    </Box>
                                                )}
                                            </Box>
                                            {/* Status pill */}
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 0.5,
                                                    px: 1.1,
                                                    py: 0.35,
                                                    borderRadius: 5,
                                                    bgcolor: meta.bg,
                                                    border: `1px solid ${meta.color}33`,
                                                    flexShrink: 0,
                                                }}>
                                                <Box sx={{ color: meta.color, display: 'flex', alignItems: 'center' }}>
                                                    {meta.icon}
                                                </Box>
                                                <Typography
                                                    sx={{
                                                        color: meta.color,
                                                        fontSize: 10.5,
                                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                        textTransform: 'capitalize',
                                                    }}>
                                                    {meta.label}
                                                </Typography>
                                            </Box>
                                        </Box>

                                        {/* Amount row */}
                                        {(p.usd_cents != null || p.amount_cents != null) && (
                                            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 0.5 }}>
                                                {p.usd_cents != null && (
                                                    <Box>
                                                        <Typography
                                                            sx={{
                                                                color: BRAND_LUXE_MUTED,
                                                                fontSize: 10,
                                                                textTransform: 'uppercase',
                                                                letterSpacing: '0.05em',
                                                            }}>
                                                            Locks
                                                        </Typography>
                                                        <Typography
                                                            sx={{
                                                                color: C_AMBER,
                                                                fontSize: 13,
                                                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                                            }}>
                                                            {fmt(p.usd_cents, 'USD')}
                                                        </Typography>
                                                    </Box>
                                                )}
                                                {p.amount_cents != null && p.currency && (
                                                    <Box>
                                                        <Typography
                                                            sx={{
                                                                color: BRAND_LUXE_MUTED,
                                                                fontSize: 10,
                                                                textTransform: 'uppercase',
                                                                letterSpacing: '0.05em',
                                                            }}>
                                                            Contract value
                                                        </Typography>
                                                        <Typography
                                                            sx={{
                                                                color: '#fff',
                                                                fontSize: 13,
                                                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                            }}>
                                                            {fmt(p.amount_cents, p.currency)}
                                                        </Typography>
                                                    </Box>
                                                )}
                                            </Box>
                                        )}
                                    </Box>

                                    {/* Payout confirmation banner */}
                                    {paid && (
                                        <Box
                                            sx={{
                                                px: 1.75,
                                                py: 1,
                                                bgcolor: 'rgba(0,253,193,0.06)',
                                                borderTop: `1px solid rgba(0,253,193,0.12)`,
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 0.75,
                                            }}>
                                            <CheckCircleOutlineIcon
                                                sx={{ fontSize: 14, color: C_TEAL, flexShrink: 0 }}
                                            />
                                            <Typography sx={{ color: C_TEAL, fontSize: 12 }}>{paid}</Typography>
                                        </Box>
                                    )}

                                    {/* Failure reason */}
                                    {p.failure_reason && (
                                        <Box
                                            sx={{
                                                px: 1.75,
                                                py: 1,
                                                bgcolor: 'rgba(255,115,81,0.06)',
                                                borderTop: `1px solid rgba(255,115,81,0.12)`,
                                                display: 'flex',
                                                alignItems: 'flex-start',
                                                gap: 0.75,
                                            }}>
                                            <ErrorOutlineIcon
                                                sx={{ fontSize: 14, color: C_ERROR, flexShrink: 0, mt: 0.15 }}
                                            />
                                            <Typography sx={{ color: C_ERROR, fontSize: 12 }}>
                                                {p.failure_reason}
                                                {p.usd_cents != null
                                                    ? ` — needs ${fmt(p.usd_cents, 'USD')} in wallet`
                                                    : ''}
                                            </Typography>
                                        </Box>
                                    )}

                                    {/* Action buttons */}
                                    {(canLock || isLocked) && (
                                        <Box
                                            sx={{
                                                px: 1.75,
                                                py: 1.25,
                                                borderTop: '1px solid rgba(255,255,255,0.05)',
                                                display: 'flex',
                                                gap: 1,
                                                flexWrap: 'wrap',
                                            }}>
                                            {canLock && (
                                                <ManageGoldButton
                                                    disabled={busy || !p._id}
                                                    onClick={() => onAction('lock', p._id)}
                                                    sx={{ fontSize: 12, py: 0.6 }}>
                                                    <LockOutlinedIcon sx={{ fontSize: 13 }} /> Lock escrow
                                                </ManageGoldButton>
                                            )}
                                            {isLocked && (
                                                <>
                                                    <Tooltip title="Pay manager now — payout also happens automatically when the period ends">
                                                        <span>
                                                            <ManageGoldButton
                                                                disabled={busy || !p._id}
                                                                onClick={() => onAction('payout', p._id)}
                                                                sx={{ fontSize: 12, py: 0.6, gap: 0.5 }}>
                                                                <CheckCircleOutlineIcon sx={{ fontSize: 13 }} /> Pay now
                                                            </ManageGoldButton>
                                                        </span>
                                                    </Tooltip>
                                                    <Tooltip title="Return funds to your brand wallet instead of paying the manager">
                                                        <span>
                                                            <ManageGoldButton
                                                                disabled={busy || !p._id}
                                                                onClick={() => onAction('release', p._id)}
                                                                sx={{
                                                                    fontSize: 12,
                                                                    py: 0.6,
                                                                    gap: 0.5,
                                                                    bgcolor: 'transparent',
                                                                    color: BRAND_LUXE_MUTED,
                                                                    border: '1px solid rgba(255,255,255,0.1)',
                                                                    '&:hover': { bgcolor: 'rgba(255,255,255,0.05)' },
                                                                }}>
                                                                <UndoOutlinedIcon sx={{ fontSize: 13 }} /> Return to
                                                                wallet
                                                            </ManageGoldButton>
                                                        </span>
                                                    </Tooltip>
                                                </>
                                            )}
                                        </Box>
                                    )}
                                </Box>
                            );
                        })}
                    </Box>
                )}
            </Box>
        </Box>
    );
}
